/**
 * Market Data Component
 * Displays VIX, SPY, and sector rotation data
 */

import { useMarketData } from '../api/client';

export function MarketData() {
  const { data, isLoading, error } = useMarketData();

  if (isLoading) {
    return (
      <div className="card">
        <div className="loading">Loading market data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="error">Error loading market data: {error.message}</div>
      </div>
    );
  }

  const market = data?.marketData;
  if (!market) return null;

  // Sort sectors by momentum score
  const topSectors = [...market.sectorRotation]
    .sort((a, b) => b.momentumScore - a.momentumScore)
    .slice(0, 5);

  const weakestSectors = [...market.sectorRotation]
    .sort((a, b) => a.momentumScore - b.momentumScore)
    .slice(0, 3);

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">Market Data</h2>
        <span className={`card-badge ${
          market.marketRegime === 'HIGH_VOLATILITY' ? 'badge-danger' :
          market.marketRegime === 'ELEVATED_VOLATILITY' ? 'badge-warning' :
          'badge-success'
        }`}>
          {market.marketRegime.replace('_', ' ')}
        </span>
      </div>

      <div className="grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
        {/* VIX */}
        <div className="metric">
          <div className="metric-label">VIX (Volatility Index)</div>
          <div
            className="metric-value"
            style={{
              color:
                market.vix > 35
                  ? 'var(--accent-red)'
                  : market.vix > 25
                  ? 'var(--accent-yellow)'
                  : 'var(--accent-green)',
            }}
          >
            {market.vix.toFixed(2)}
          </div>
        </div>

        {/* SPY */}
        <div className="metric">
          <div className="metric-label">SPY (S&P 500 ETF)</div>
          <div className="metric-value">
            ${market.spy.toFixed(2)}
            <span
              style={{
                fontSize: '0.875rem',
                marginLeft: '0.5rem',
                color: market.spyChange >= 0 ? 'var(--accent-green)' : 'var(--accent-red)',
              }}
            >
              {market.spyChange >= 0 ? '+' : ''}${market.spyChange.toFixed(2)} ({market.spyChangePct >= 0 ? '+' : ''}
              {market.spyChangePct.toFixed(2)}%)
            </span>
          </div>
        </div>
      </div>

      {/* Sector Rotation */}
      <div style={{ marginTop: '1.5rem' }}>
        <div style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem' }}>
          Top Performing Sectors
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {topSectors.map((sector) => (
            <div
              key={sector.sector}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.5rem',
                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                borderRadius: '6px',
                fontSize: '0.875rem',
              }}
            >
              <div>
                <strong>{sector.sector}</strong> ({sector.etfTicker})
              </div>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <span
                  style={{
                    color:
                      sector.dailyChangePct >= 0 ? 'var(--accent-green)' : 'var(--accent-red)',
                  }}
                >
                  {sector.dailyChangePct >= 0 ? '+' : ''}
                  {sector.dailyChangePct.toFixed(2)}%
                </span>
                <span
                  className="card-badge"
                  style={{
                    backgroundColor:
                      sector.category === 'STRONG'
                        ? 'rgba(16, 185, 129, 0.1)'
                        : sector.category === 'MODERATE'
                        ? 'rgba(59, 130, 246, 0.1)'
                        : 'rgba(245, 158, 11, 0.1)',
                    color:
                      sector.category === 'STRONG'
                        ? 'var(--accent-green)'
                        : sector.category === 'MODERATE'
                        ? 'var(--accent-blue)'
                        : 'var(--accent-yellow)',
                  }}
                >
                  {sector.category}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Rotating Out Sectors */}
      {weakestSectors.filter((s) => s.isRotatingOut).length > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <div style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem', color: 'var(--accent-red)' }}>
            Sectors Rotating Out
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {weakestSectors
              .filter((s) => s.isRotatingOut)
              .map((sector) => (
                <div
                  key={sector.sector}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.5rem',
                    backgroundColor: 'rgba(239, 68, 68, 0.05)',
                    border: '1px solid rgba(239, 68, 68, 0.2)',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                  }}
                >
                  <div>
                    <strong>{sector.sector}</strong> ({sector.etfTicker})
                  </div>
                  <div style={{ color: 'var(--accent-red)' }}>
                    {sector.dailyChangePct.toFixed(2)}%
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      <div style={{ marginTop: '1rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
        Last updated: {new Date(market.timestamp).toLocaleTimeString('en-US')}
      </div>
    </div>
  );
}
